import { KeyboardEvent, ReactNode, useState } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Drawer from '@mui/material/Drawer'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ArrowUpRight as ArrowUpRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { Link as RouterLink } from 'react-router-dom'

import type { NavItemConfig } from '@/types/nav'
import { paths } from '@/paths'
import { isNavItemActive } from '@/lib/is-nav-item-active'
import { usePathname } from '@/hooks/use-pathname'
import { useSettings } from '@/hooks/use-settings'
import { useUser } from '@/hooks/use-user'
import { useWorkspaces } from '@/hooks/use-workspaces'
import { Logo } from '@/components/core/logo'

import { logoColors, mobileNavColorStyles } from '../styles'
import { WorkspacesSwitch } from '../workspaces/workspaces-switch'
import { icons } from './nav-icons'
import { NavTypeformItem } from './nav-typeform-item'

export interface MobileNavProps {
	onClose?: () => void
	open?: boolean
	items?: NavItemConfig[]
}

export function MobileNav({ items = [], open, onClose }: MobileNavProps) {
	const pathname = usePathname()

	const {
		settings: { colorScheme = 'light' },
	} = useSettings()

	const styles = mobileNavColorStyles[colorScheme]
	const logoColor = logoColors[colorScheme]

	return (
		<Drawer
			PaperProps={{
				'data-test': 'mobile-nav',
				sx: {
					...styles,
					bgcolor: 'var(--MobileNav-background)',
					color: 'var(--MobileNav-color)',
					display: 'flex',
					flexDirection: 'column',
					maxWidth: '100%',
					width: 'var(--MobileNav-width)',
					zIndex: 'var(--MobileNav-zIndex)',
					scrollbarWidth: 'none',
					'&::-webkit-scrollbar': { display: 'none' },
				},
			}}
			onClose={onClose}
			open={open}
		>
			<Stack spacing={2} sx={{ p: 2 }}>
				<div>
					<Box component={RouterLink} to={paths.home} sx={{ display: 'inline-flex' }}>
						<Logo color={logoColor} height={31.5} width="auto" />
					</Box>
				</div>
				<WorkspacesSwitch />
			</Stack>
			<Box component="nav" sx={{ flex: '1 1 auto', p: 2 }}>
				{renderNavGroups({ items, onClose, pathname })}
			</Box>
		</Drawer>
	)
}

function renderNavGroups({
	items,
	onClose,
	pathname,
}: {
	items: NavItemConfig[]
	onClose?: () => void
	pathname: string
}) {
	const children = items.reduce((acc: ReactNode[], curr: NavItemConfig): ReactNode[] => {
		acc.push(
			<Stack component="li" key={curr.key} spacing={1.5}>
				{curr.title ? (
					<div>
						<Typography sx={{ color: 'var(--NavGroup-title-color)', fontSize: '0.875rem', fontWeight: 500 }}>
							{curr.title}
						</Typography>
					</div>
				) : null}
				<div>{renderNavItems({ depth: 0, items: curr.items, onClose, pathname })}</div>
			</Stack>
		)

		return acc
	}, [])

	return (
		<Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
			{children}
		</Stack>
	)
}

function renderNavItems({
	depth = 0,
	items = [],
	onClose,
	pathname,
}: {
	depth: number
	items?: NavItemConfig[]
	onClose?: () => void
	pathname: string
}) {
	const children = items.reduce((acc: ReactNode[], curr: NavItemConfig): ReactNode[] => {
		const { items: childItems, key, ...item } = curr

		if (curr.typeformId) {
			acc.push(
				<NavTypeformItem
					key={key}
					typeformId={curr.typeformId}
					title={curr.title}
					depth={depth}
					onClose={onClose}
				/>
			)
			return acc
		}

		const forceOpen = childItems
			? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
			: false

		acc.push(
			<NavItem depth={depth} forceOpen={forceOpen} key={key} onClose={onClose} pathname={pathname} {...item}>
				{childItems ? renderNavItems({ depth: depth + 1, items: childItems, onClose, pathname }) : null}
			</NavItem>
		)

		return acc
	}, [])

	return (
		<Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
			{children}
		</Stack>
	)
}

interface NavItemProps extends Omit<NavItemConfig, 'items'> {
	children?: ReactNode
	depth: number
	forceOpen?: boolean
	onClose?: () => void
	pathname: string
}

function NavItem({
	children,
	depth,
	disabled,
	external,
	forceOpen = false,
	href,
	icon,
	label,
	matcher,
	onClose,
	pathname,
	title,
	requiresBetaAccess,
	comingSoon = false,
}: NavItemProps) {
	const [open, setOpen] = useState<boolean>(forceOpen)
	const { user } = useUser()
	const { hasBetaFeaturesEnabled } = useWorkspaces()

	if (requiresBetaAccess && !hasBetaFeaturesEnabled) {
		/* eslint-disable no-param-reassign -- Expected */
		disabled = true
		comingSoon = true
		href = undefined
		/* eslint-enable no-param-reassign -- Expected */
	}

	const active = isNavItemActive({ disabled, external, href, matcher, pathname })
	const Icon = icon ? icons[icon] : null
	const ExpandIcon = open ? CaretDownIcon : CaretRightIcon
	const isBranch = children && !href
	const showChildren = Boolean(children && open)
	const showLabel = requiresBetaAccess ? hasBetaFeaturesEnabled : true

	return (
		<Box component="li" data-depth={depth} sx={{ userSelect: 'none' }}>
			<Box
				data-test="mobile-nav-item"
				{...(isBranch
					? {
							onClick: (): void => {
								setOpen(!open)
							},
							onKeyUp: (event: KeyboardEvent<HTMLElement>): void => {
								if (event.key === 'Enter' || event.key === ' ') {
									setOpen(!open)
								}
							},
							role: 'button',
						}
					: {
							...(href
								? {
										component: external ? 'a' : RouterLink,
										href: external ? href : undefined,
										to: external ? undefined : href,
										target: external ? '_blank' : undefined,
										rel: external ? 'noreferrer' : undefined,
										onClick: (): void => {
											onClose?.()
										},
									}
								: { role: 'button' }),
						})}
				sx={{
					alignItems: 'center',
					borderRadius: 1,
					color: 'var(--NavItem-color)',
					cursor: 'pointer',
					display: 'flex',
					flex: '0 0 auto',
					gap: 1,
					p: '6px 16px',
					position: 'relative',
					textDecoration: 'none',
					whiteSpace: 'nowrap',
					...(disabled && {
						bgcolor: 'var(--NavItem-disabled-background)',
						color: 'var(--NavItem-disabled-color)',
						cursor: 'not-allowed',
					}),
					...(active && {
						bgcolor: 'var(--NavItem-active-background)',
						color: 'var(--NavItem-active-color)',
						...(depth > 0 && {
							'&::before': {
								bgcolor: 'var(--NavItem-children-indicator)',
								borderRadius: '2px',
								content: '" "',
								height: '20px',
								left: '-14px',
								position: 'absolute',
								width: '3px',
							},
						}),
					}),
					...(open && { color: 'var(--NavItem-open-color)' }),
					'&:hover': {
						...(!disabled &&
							!active && {
								bgcolor: 'var(--NavItem-hover-background)',
								color: 'var(--NavItem-hover-color)',
							}),
					},
				}}
				tabIndex={0}
			>
				<Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
					{Icon ? (
						<Icon
							fill={active ? 'var(--NavItem-icon-active-color)' : 'var(--NavItem-icon-color)'}
							fontSize="var(--icon-fontSize-md)"
							weight={forceOpen || active ? 'fill' : undefined}
						/>
					) : null}
				</Box>
				<Box sx={{ flex: '1 1 auto', height: '28px', minWidth: 0 }}>
					<Typography
						component="span"
						noWrap
						sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
					>
						{title}
					</Typography>
				</Box>
				{external ? (
					<Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto', ml: 'auto' }}>
						<ArrowUpRightIcon fontSize="var(--icon-fontSize-sm)" />
					</Box>
				) : null}
				{showLabel && label ? <Chip color="primary" label={label} size="small" /> : null}
				{isBranch ? (
					<Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
						<ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
					</Box>
				) : null}
			</Box>
			{showChildren ? (
				<Box sx={{ pl: '24px' }}>
					<Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
				</Box>
			) : null}
		</Box>
	)
}
